import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase/client';
import { mode } from '@/lib/config/app-mode';

/**
 * Mint a short-lived signed URL for a feedback screenshot.
 *
 * `feedback-screenshots` is a PRIVATE bucket — screenshots routinely capture
 * PII and other users' data, so the bucket was flipped private in
 * `20260729140000_private_feedback_screenshots.sql`. `feedback.screenshot_path`
 * therefore stores the storage PATH (`{user_id}/{ts}.jpg`), not a URL: a public
 * URL would not resolve, and a signed URL persisted in the row would expire.
 *
 * Storage RLS scopes reads to the screenshot's owner or a super_admin/support
 * admin, so this resolves only for callers already entitled to the feedback row.
 */
const SIGNED_URL_TTL_SECONDS = 300;

export function useScreenshotUrl(path: string | null | undefined) {
  return useQuery({
    queryKey: ['feedback-screenshot-url', path],
    enabled: Boolean(path),
    // Re-sign a little before expiry so a long-lived detail screen never
    // renders a dead URL.
    staleTime: (SIGNED_URL_TTL_SECONDS - 30) * 1000,
    queryFn: async (): Promise<string | null> => {
      if (!path) return null;

      // Mock/sample rows carry absolute URLs rather than storage paths.
      if (path.startsWith('http://') || path.startsWith('https://')) return path;
      if (mode.showSampleData) return null;

      const { data, error } = await supabase.storage
        .from('feedback-screenshots')
        .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);

      if (error) throw error;
      return data.signedUrl;
    },
  });
}
